import React, { useContext, useState } from 'react'
import { ListMenu, ListMenuButton, Modal, TabMenu, TabMenuButton, Text, View } from 'oio-react'
import { Route, Switch, useHistory, useParams, useRouteMatch } from 'react-router-dom'
import ArrowDownIcon from 'assets/icons/arrowDown'
import CloseIcon from 'assets/icons/close'
import SessionListItem from 'src/components/Schedule/SessionListItem'
import { useEventSessionDateGrouping, useLocationQuery } from 'src/components/utils'
import ScrollView from 'src/components/utils/ScrollView'
import { useEventSessionList } from 'src/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'
import Session from './Session'

const InitiativeSchedule = () => {
   const [sessionModalIsOpen, setSessionModalIsOpen] = useState(true)
   const [labelPickerModalIsOpen, setLabelPickerModalIsOpen] = useState(false)

   const history = useHistory()
   const match = useRouteMatch()
   const { labelId } = useLocationQuery()
   const { selectedDateKey: initialSelectedDateKey } = useParams()

   const { initiative: event } = useContext(InitiativeHierarchyContext)
   const { sessionList, networkStatus } = useEventSessionList(event)

   // Because sessionList is already ordered, we do not need to worry about the order
   // of the day labels nor the order of sessions each day.
   const [sessionDates, sessionsGroupedByDate] = useEventSessionDateGrouping(sessionList)

   const baseUrl = initialSelectedDateKey
      ? match.url.split('/').slice(0, -1).join('/')
      : match.url

   let selectedDateKey = null
   let selectedDateSessions = []
   if (networkStatus === 7) {
      try {
         selectedDateKey = initialSelectedDateKey || Object.keys(sessionsGroupedByDate)[0]
         selectedDateSessions = selectedDateKey ? sessionsGroupedByDate[selectedDateKey] : []
      } catch (err) {} // eslint-disable-line no-empty
   }

   const scheduleHasLabels = event.appsScheduleLabels.length > 0
   const activeLabelObj = event.appsScheduleLabels.find(label => label.id === labelId)
   // If a labelId has been passed to the query params, then filter the sessions
   let labelIdQueryString = ''
   if (labelId) {
      labelIdQueryString = `?labelId=${labelId}`
      selectedDateSessions = selectedDateSessions.filter(session => (
         session.labels.some(label => label.id === labelId)
      ))
   }

   if (!initialSelectedDateKey && selectedDateKey && networkStatus === 7) {
      history.replace(`${baseUrl}/${selectedDateKey}`)
      return null
   }

   return (
      <View width="100%">
         <View
            display="flex"
            alignItems="center"
            width="100%"
            height="72px"
            borderBottom="1px solid #ddd">
            <View flex="1 1 auto">
               <Text size="5" weight="medium">
                  Schedule
               </Text>
            </View>
         </View>
         <View
            display="block[a-b] none[c-f]"
            width="100%">
            <TabMenu>
               {sessionDates.map(sessionDate => (
                  <TabMenuButton
                     key={sessionDate.dateKey}
                     name={sessionDate.nameShort}
                     linkTo={`${baseUrl}/${sessionDate.dateKey}`}
                  />
               ))}
            </TabMenu>
            {scheduleHasLabels && (
               <>
                  <View
                     onClick={() => setLabelPickerModalIsOpen(true)}
                     display="flex"
                     justifyContent="space-between"
                     alignItems="center"
                     float="left"
                     width="100%"
                     padding="9px 12px"
                     backgroundColor="#eee"
                     borderRadius="3px"
                     marginTop="12px">
                     <Text size="1.5" color="#666">
                        Showing:
                        {activeLabelObj
                           ? ` ${activeLabelObj.name}`
                           : ' All Topics'
                        }
                     </Text>
                     <ArrowDownIcon
                        width="10px"
                        height="10px"
                        color="#666"
                        strokeWidth="4px"
                     />
                  </View>
                  <Modal
                     borderRadius="6px"
                     width="300px"
                     height="300px"
                     onCloseTrigger={() => setLabelPickerModalIsOpen(false)}
                     open={labelPickerModalIsOpen}>
                     <View
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        padding="0px 18px"
                        width="100%"
                        height="42px"
                        borderBottom="1px solid #eee">
                        <Text weight="medium" color="#333" size="2">
                           Filter Topics:
                        </Text>
                        <View onClick={() => setLabelPickerModalIsOpen(false)}>
                           <CloseIcon
                              width="15px"
                              height="15px"
                              strokeWidth="2px"
                              color="#555"
                           />
                        </View>
                     </View>
                     {/* Note: This is not optimal, but we make scrollview
                        conditional so it does not interfere with other View
                        components with scroll behaviour */}
                     {labelPickerModalIsOpen && (
                        <ScrollView
                           onClick={() => setLabelPickerModalIsOpen(false)}
                           width="100%"
                           height="100%"
                           scroll="on">
                           <ListMenu
                              activeBackgroundColor="#eee"
                              activeTextColor="#333"
                              buttonBorderRadius="4px"
                              buttonSize="md"
                              buttonTextSize="1.5"
                              buttonPaddingHorizontal="18px"
                              dividerLineStyle="none">
                              <ListMenuButton
                                 isActive={!labelId}
                                 name="All"
                                 linkTo={match.url}
                                 leftDetail={(
                                    <View
                                       width="9px"
                                       height="9px"
                                       borderRadius="50%"
                                       border="1px solid #aaa"
                                    />
                                 )}
                              />
                              {event.appsScheduleLabels.map(label => (
                                 <ListMenuButton
                                    key={label.id}
                                    isActive={label.id === labelId}
                                    name={label.name}
                                    linkTo={`${match.url}?labelId=${label.id}`}
                                    leftDetail={(
                                       <View
                                          width="9px"
                                          height="9px"
                                          borderRadius="50%"
                                          backgroundColor={label.color}
                                       />
                                    )}
                                 />
                              ))}
                           </ListMenu>
                        </ScrollView>
                     )}
                  </Modal>
               </>
            )}
         </View>
         {sessionDates.length === 0 && (
            <View width="100%" textAlign="center" padding="30px 0px">
               <Text size="2" color="#666">
                  {`${event.type.nameSingular} Schedule is coming soon`}
               </Text>
            </View>
         )}
         <View display="flex" width="100%">
            <View
               display="none[a-b] block[c-f]"
               width="180px"
               flex="0 0 auto"
               paddingTop="12px"
               paddingRight="30px">
               {scheduleHasLabels && sessionDates.length > 1 && (
                  <View margin="12px 12px 6px 12px">
                     <Text size="0.9" weight="bold" color="#aaa" transform="uppercase">
                        Select Date
                     </Text>
                  </View>
               )}
               <ListMenu
                  activeBackgroundColor="#222"
                  activeTextColor="#fff"
                  buttonBorderRadius="4px"
                  buttonSize="sm"
                  buttonTextSize="1.5"
                  buttonPaddingHorizontal="12px"
                  dividerLineStyle="none"
                  showActiveArrow>
                  {sessionDates.map(sessionDate => (
                     <ListMenuButton
                        key={sessionDate.dateKey}
                        isActive={selectedDateKey === sessionDate.dateKey}
                        name={sessionDate.nameFull}
                        linkTo={`${baseUrl}/${sessionDate.dateKey}${labelIdQueryString}`}
                     />
                  ))}
               </ListMenu>
               {scheduleHasLabels && (
                  <View
                     float="left"
                     borderTop="1px solid #eee"
                     margin="12px 0px">
                     <View margin="12px 12px 6px 12px">
                        <Text size="0.9" weight="bold" color="#aaa" transform="uppercase">
                           Filter by Topic
                        </Text>
                     </View>
                     <ListMenu
                        activeBackgroundColor="#eee"
                        activeTextColor="#333"
                        buttonBorderRadius="4px"
                        buttonSize="sm"
                        buttonTextSize="1"
                        buttonPaddingHorizontal="12px"
                        dividerLineStyle="none">
                        <ListMenuButton
                           isActive={!labelId}
                           name="All"
                           linkTo={match.url}
                           leftDetail={(
                              <View
                                 width="9px"
                                 height="9px"
                                 borderRadius="50%"
                                 border="1px solid #aaa"
                              />
                           )}
                        />
                        {event.appsScheduleLabels.map(label => (
                           <ListMenuButton
                              key={label.id}
                              isActive={label.id === labelId}
                              name={label.name}
                              linkTo={`${match.url}?labelId=${label.id}`}
                              leftDetail={(
                                 <View
                                    width="9px"
                                    height="9px"
                                    borderRadius="50%"
                                    backgroundColor={label.color}
                                 />
                              )}
                           />
                        ))}
                     </ListMenu>
                  </View>
               )}
            </View>
            <View flex="1 1 auto">
               {selectedDateSessions.map((session, i) => (
                  <SessionListItem
                     key={session.id}
                     borderStyle={i === 0 ? 'none' : undefined}
                     endTime={session.endTime}
                     labels={session.labels}
                     location={session.location?.name}
                     linkTo={`${baseUrl}/${selectedDateKey}/session/${session.slug}`}
                     name={session.name}
                     startTime={session.startTime}
                  />
               ))}
            </View>
         </View>
         <Switch>
            <Route
               path={`${baseUrl}/${selectedDateKey}/session/:sessionSlug`}
               render={props => (
                  <Modal
                     borderRadius="9px"
                     overlayBackgroundColor="rgba(20,20,20,0.9)"
                     width="100%[a-b] 90%[c] 660px[d-f]"
                     height="100%[a-b] 90%[c-f]"
                     onCloseComplete={() => {
                        history.push(match.url)
                        setSessionModalIsOpen(true)
                     }}
                     onCloseTrigger={() => setSessionModalIsOpen(false)}
                     open={sessionModalIsOpen}>
                     <Session />
                  </Modal>
               )}
            />
         </Switch>
      </View>
   )
}

export default InitiativeSchedule
